import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  ReactNode,
  MutableRefObject,
} from 'react'

import MenuPopover from '@/modules/layout/components/menuV2/menu-popover/menu-popover'
import MenuPopoverItem, { SubmenuItemInterface } from '@/modules/layout/components/menuV2/menu-popover/menu-popover-item'

interface MenuPopoverContextType {
  target: MutableRefObject<any> | undefined
  isOpen: boolean
  setOpen: (open: boolean) => void
  setTarget: (target: MutableRefObject<any>) => void
  setMenuItems: (items: SubmenuItemInterface[]) => void
}

const MenuPopoverContext = createContext<MenuPopoverContextType | undefined>(undefined)

export const MenuPopoverProvider = ({ children }: { children: ReactNode }) => {
  const [target, setCurrentTarget] = useState<MutableRefObject<any> | undefined>(undefined)
  const [items, setItems] = useState<SubmenuItemInterface[]>([])
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const setTarget = (target: MutableRefObject<any>) => setCurrentTarget(target)
  const setOpen = (open: boolean) => setIsOpen(open)
  const setMenuItems = (items: SubmenuItemInterface[]) => setItems(items)

  const menuItems = useMemo(() => {
    if (!items?.length) return []
    // names are not guaranteed unique, and on a duplicate key react keeps the previous menu's
    // items around instead of replacing them
    return items.map((menuItem, index) => (
      <MenuPopoverItem key={`${menuItem.name}-${index}`} {...menuItem} />
    ))
  }, [items])

  const popover = useMemo(() => {
    if (!menuItems?.length || !isOpen) { return null }
    return (
      <MenuPopover
        anchorEl={target?.current}
        open={isOpen}
        onClose={() => setIsOpen(false)}
      >
        {menuItems}
      </MenuPopover>
    )
  }, [isOpen, target, menuItems])

  return (
    <MenuPopoverContext.Provider
      value={{
        target,
        isOpen,
        setOpen,
        setTarget,
        setMenuItems,
      }}
    >
      {children}
      {popover}
    </MenuPopoverContext.Provider>
  )
}

export default function useMenuPopover(): MenuPopoverContextType {
  const context = useContext(MenuPopoverContext)
  if (!context) {
    throw new Error('useMenuPopover must be used within a MenuPopoverProvider')
  }
  return context
}
